import { ChangeDetectionStrategy, Component, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, Subscription, finalize, first, firstValueFrom, merge, switchMap, tap } from 'rxjs';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { ScreenService } from 'src/app/core/screen.service';
import { OrderService } from 'src/app/data/order.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { OrdersRealizationListDataSource } from './orders-realization-list.datasource';
import { Messages, Permission } from 'src/app/common/enums';
import { OrderItemStatus } from 'src/app/models/enums/order-item-status';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { OrderRealizationItemsDto } from 'src/app/models/dtos/orders/order-realization-items-dto';
import { DateTime } from 'luxon';

const DEFAULT_SORT_FIELD = 'SubmissionDate';

@Component({
  selector: 'app-orders-realization-list',
  templateUrl: './orders-realization-list.component.html',
  styleUrl: './orders-realization-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OrdersRealizationListDataSource],
  standalone: false
})
export class OrdersRealizationListComponent {
  screenService = inject(ScreenService);
  dataSource = inject(OrdersRealizationListDataSource);
  private translateService = inject(TranslateService);
  private spinner = inject(NgxSpinnerService);
  private orderService = inject(OrderService);
  private dialog = inject(MatDialog);
  private snackbar = inject(SnackBarService);
  private authService = inject(AuthService);

  displayedColumns = [
    'clientCode',
    'employerName',
    'employerObjectName',
    'positionExpectationName',
    'submissionDate',
    'startDate',
    'employeesNumber',
    'realizationPeriodNumber',
    'finalRealizationNumber',
    'actions',
  ];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private subscriptions: Subscription[] = [];
  currentDate: DateTime;

  constructor() { }

  ngOnInit(): void {
    this.currentDate = DateTime.now();
  }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length) {
      this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
  }

  ngAfterViewInit(): void {
    this.sort.sort({ id: DEFAULT_SORT_FIELD, start: 'desc' } as MatSortable);

    this.subscriptions.push(this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0)));
    this.subscriptions.push(this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0)));

    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange).subscribe(() => this.fetchOrderRealizationItems()),
    );

    this.subscriptions.push(
      this.dataSource.loading$.subscribe({
        next: (isLoading) => (isLoading ? this.spinner.show() : this.spinner.hide()),
        complete: () => this.spinner.hide(),
      }),
    );

    this.fetchOrderRealizationItems();
  }

  previousWeek(): void {
    this.currentDate = this.currentDate.minus({ weeks: 1 });
    this.fetchOrderRealizationItems();
  }

  nextWeek(): void {
    this.currentDate = this.currentDate.plus({ weeks: 1 });
    this.fetchOrderRealizationItems();
  }

  onDateChange(event): void {
    this.currentDate = DateTime.fromJSDate(event.value.toDate());
    this.fetchOrderRealizationItems();
  }

  hasArchiveRestorePermission() {
    return this.authService.hasPermission(Permission.ArchiveRestoreOrderItem);
  }

  archiveButtonVisible = (orderItemStatusId: number): boolean => orderItemStatusId === OrderItemStatus.Active && this.hasArchiveRestorePermission();

  hasAnyAction(order: OrderRealizationItemsDto): boolean {
    return this.archiveButtonVisible(order.OrderItemStatusId);
  }

  async archiveOrderItem(orderItemId: number) {
    const onConfirm = () => {
      this.spinner.show();
      return this.orderService.archiveOrderItem(orderItemId).pipe(
        first(),
        tap((_) => this.fetchOrderRealizationItems()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyArchivedOrder)),
        finalize(() => this.spinner.hide()),
      );
    };

    await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: Messages.ArchiveOrderItemTitle,
            message: Messages.ArchiveOrderItemMessage,
            confirmButtonLabel: this.translateService.instant(Messages.ArchiveOrderItemConfirmButton),
            dismissButtonLabel: this.translateService.instant(Messages.OrderDeclineButton),
          },
        })
        .afterClosed()
        .pipe(
          first(),
          switchMap((isConfirmed) => (isConfirmed ? onConfirm() : EMPTY)),
        ),
    );
  }

  private fetchOrderRealizationItems(resetPage: boolean = false) {
    if (resetPage) {
      this.paginator.pageIndex = 0;
    }

    const start = this.currentDate.startOf('week');
    const end = this.currentDate.endOf('week');

    this.dataSource.fetchOrderRealizationItems(
      start.toJSDate(),
      end.toJSDate(),
      this.paginator.pageIndex + 1,
      this.paginator.pageSize,
      this.sort.active,
      this.sort.direction,
    );
  }
}
